import { queryOptions, useQuery } from '@tanstack/react-query';
import type { CdpOutIntegration, DataOutIntegration, MParticleIntegration } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { integrationKeys } from './entity';

/**
 * Single-object integration status reads (mParticle, CDP out, Segment in/out,
 * data-out). The "not configured" case is a fetch error that resolves to
 * `null`/`false` rather than surfacing, so the directory shows the integration
 * as simply not-enabled.
 */

const STALE_TIME = 60_000;

const statusKey = (workspaceId: number, name: string) => [...integrationKeys.all(workspaceId), 'status', name] as const;

/* ------------------------------- mParticle ------------------------------- */

/** @public The mParticle integration config, or `null` when not configured. */
export async function getMparticleIntegration(workspaceId: number): Promise<MParticleIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getMparticle({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data.mparticle ?? null;
  } catch {
    return null;
  }
}

export const getMparticleIntegrationQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'mparticle'),
    queryFn: () => getMparticleIntegration(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useMparticleIntegration = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getMparticleIntegrationQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getMparticleIntegrationQueryOptions(workspaceId), ...queryConfig });
};

/* --------------------------------- CDP out ------------------------------- */

/** @public Whether a CDP-out integration is configured (drives CDP in + out). */
export async function getCdpOutEnabled(workspaceId: number): Promise<boolean> {
  try {
    const data = await hydraUiApi.integrations.getCdpOut({
      params: { path: { environment_id: String(workspaceId) } },
    });
    const cdpOut: CdpOutIntegration | undefined = data.cdp_out;
    return cdpOut != null;
  } catch {
    return false;
  }
}

export const getCdpOutEnabledQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'cdp_out'),
    queryFn: () => getCdpOutEnabled(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useCdpOutEnabled = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getCdpOutEnabledQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getCdpOutEnabledQueryOptions(workspaceId), ...queryConfig });
};

/* ------------------------------- Segment in ------------------------------ */

/** @public Whether the Segment (Destination) integration is present. */
export async function getSegmentInPresent(workspaceId: number): Promise<boolean> {
  try {
    await hydraUiApi.integrations.getSegmentIn({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return true;
  } catch {
    return false;
  }
}

export const getSegmentInPresentQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'segment_in'),
    queryFn: () => getSegmentInPresent(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useSegmentInPresent = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentInPresentQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentInPresentQueryOptions(workspaceId), ...queryConfig });
};

/* ------------------------- Segment out (segdotcom) ----------------------- */

/**
 * @public Whether the Segment (Source) integration is enabled.
 *
 * Unlike its `segment_out` sibling this response is not enveloped — the fields
 * sit at the top level.
 */
export async function getSegmentOutEnabled(workspaceId: number): Promise<boolean> {
  try {
    const data = await hydraUiApi.integrations.getSegdotcom({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return data?.enabled ?? false;
  } catch {
    return false;
  }
}

export const getSegmentOutEnabledQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: statusKey(workspaceId, 'segdotcom'),
    queryFn: () => getSegmentOutEnabled(workspaceId),
    staleTime: STALE_TIME,
  });

/** @public */
export const useSegmentOutEnabled = ({
  queryConfig,
}: { queryConfig?: QueryConfig<typeof getSegmentOutEnabledQueryOptions> } = {}) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({ ...getSegmentOutEnabledQueryOptions(workspaceId), ...queryConfig });
};

/* -------------------------------- Data out ------------------------------- */

/**
 * @public Whether a NativeDataOut integration (amplitude / mixpanel) is enabled.
 * A successful `data_out/:identifier` fetch means enabled; a 404 means not.
 */
export async function getDataOutEnabled(workspaceId: number, identifier: string): Promise<boolean> {
  try {
    const data = await hydraUiApi.integrations.getDataOut({
      params: { path: { environment_id: String(workspaceId), identifier } },
    });
    const dataOut: DataOutIntegration | undefined = data.data_out;
    return dataOut != null;
  } catch {
    return false;
  }
}

export const getDataOutEnabledQueryOptions = (workspaceId: number, identifier: string) =>
  queryOptions({
    queryKey: statusKey(workspaceId, `data_out:${identifier}`),
    queryFn: () => getDataOutEnabled(workspaceId, identifier),
    staleTime: STALE_TIME,
  });
